/**
 * Phase 11 Beat 3 — Staff Change Request Review Queue page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ChangeRequestReviewModal from "@/components/ChangeRequestReviewModal";
import { ClockCounterClockwise, CheckCircle, XCircle, ChatCentered, ArrowsClockwise } from "@phosphor-icons/react";

const STATUS_FILTERS = [
  { key: "pending_review", label: "Pending", icon: ClockCounterClockwise, tone: "amber" },
  { key: "approved", label: "Approved", icon: CheckCircle, tone: "emerald" },
  { key: "rejected", label: "Rejected", icon: XCircle, tone: "red" },
  { key: "clarification_requested", label: "Clarification", icon: ChatCentered, tone: "sky" },
  { key: "resubmitted", label: "Resubmitted", icon: ArrowsClockwise, tone: "indigo" },
];

export default function ChangeRequestQueue() {
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const [status, setStatus] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/change-requests?status=${status}`);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { pending_review: 0, approved: 0, rejected: 0, clarification_requested: 0, resubmitted: 0 };
    rows.forEach((r) => { if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="cr-queue-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Candidate Lifecycle</div>
        <h2 className="text-2xl font-semibold">Profile Change Requests</h2>
        <p className="text-sm text-slate-500">Review candidate-initiated profile updates. Approvals sync the profile, marketing module, reports, and dashboards.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        {STATUS_FILTERS.map((f) => {
          const active = status === f.key;
          const toneMap = {
            amber: "bg-amber-50 text-amber-800 border-amber-200",
            emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
            red: "bg-red-50 text-red-800 border-red-200",
            sky: "bg-sky-50 text-sky-800 border-sky-200",
            indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
          };
          const Icon = f.icon;
          return (
            <button key={f.key} onClick={() => setStatus(f.key)}
                    data-testid={`cr-filter-${f.key}`}
                    className={`text-sm rounded-lg border px-3 py-1.5 inline-flex items-center gap-1.5 font-medium transition
                                ${active ? toneMap[f.tone] : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              <Icon size={14} /> {f.label}
              {active && rows.length > 0 && (
                <span className="ml-1 text-[10px] font-semibold bg-white/70 rounded-full px-1.5 py-0.5">{rows.length}</span>
              )}
            </button>
          );
        })}
        <button onClick={load} className="text-xs text-slate-500 hover:text-sky-700 ml-auto inline-flex items-center gap-1"
                data-testid="cr-refresh">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading && <div className="text-sm text-slate-400 p-4">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-slate-400 p-8 text-center" data-testid="cr-empty">
            No change requests in <span className="font-medium">{status.replace("_", " ")}</span>.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5">Submitted</th>
                <th className="text-left px-4 py-2.5">Candidate</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Fields</th>
                <th className="text-left px-4 py-2.5">Reviewer</th>
                <th className="text-left px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((cr) => (
                <tr key={cr.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cr-row-${cr.id}`}>
                  <td className="px-4 py-2.5 text-xs">{(cr.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{cr.candidate?.full_name || cr.candidate_id?.slice(0, 8)}</div>
                    <div className="text-[10px] text-slate-500">{cr.candidate?.email || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 capitalize">{cr.category}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {Object.keys(cr.fields || {}).slice(0, 3).join(", ")}
                    {Object.keys(cr.fields || {}).length > 3 && ` +${Object.keys(cr.fields || {}).length - 3}`}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{cr.reviewer?.email || "—"}</td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => setSelected(cr)} data-testid={`cr-open-${cr.id}`}
                            className="text-sm text-sky-700 hover:text-sky-900 font-medium">
                      Review →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <ChangeRequestReviewModal
          crId={selected.id}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
